import { fetchData } from '@/lib/api/fetchData';
import type {
  CountryFilterResponse,
  DepartmentResponse,
  EuralResponse,
  FetchFilterOptions,
  FilterFetchParams,
  FilterQueryParams,
  LocationResponse,
  MaterialResponse,
  ProcessorResponse,
  ProcessResponse,
  ProjectResponse,
  WasteGroupResponse,
} from '@/lib/api/types/filters';
import { isDateRange } from '@/lib/filters/dateRange';
import type { NewDefaultFilters, NewDefaultFiltersData, NewFilters } from '@/lib/filters/types';
import type { DateRange } from '@/utils';

import { createFilterParams } from './createFilterParams';

function fetchDefaultFilter<T>(
  endpoint: string,
  params: FilterQueryParams,
  options?: FetchFilterOptions
): Promise<T | undefined> {
  return fetchData<T>(endpoint, { params: createFilterParams(params, options) }, true);
}

/** Default filters */
async function fetchCountries({ group, filters, lang }: FilterFetchParams) {
  return fetchDefaultFilter<CountryFilterResponse>('country_filter/', { group, filters, lang });
}

async function fetchDisposers({ group, filters, lang }: FilterFetchParams) {
  return fetchDefaultFilter<DepartmentResponse | unknown>('company_filter/', { group, filters, lang });
}

async function fetchEurals({ group, filters, lang }: FilterFetchParams) {
  return fetchDefaultFilter<EuralResponse | unknown>('eural_filter/', { group, filters, lang });
}

async function fetchLocations({ group, filters, lang }: FilterFetchParams) {
  return fetchDefaultFilter<LocationResponse | unknown>('location_filter/', { group, filters, lang });
}

async function fetchMaterials({ group, filters, lang }: FilterFetchParams) {
  return fetchDefaultFilter<MaterialResponse | unknown>('material_filter/', { group, filters, lang });
}

async function fetchPeriod({ group, filters, lang }: FilterFetchParams) {
  return fetchDefaultFilter<DateRange>('period_filter/', { group, filters, lang });
}

async function fetchProcesses({ group, filters, lang }: FilterFetchParams) {
  return fetchDefaultFilter<ProcessResponse | unknown>('process_filter/', { group, filters, lang });
}

async function fetchProcessors({ group, filters, lang }: FilterFetchParams) {
  return fetchDefaultFilter<ProcessorResponse | unknown>('processor_filter/', { group, filters, lang });
}

async function fetchProjects({ group, filters }: FilterFetchParams) {
  return fetchDefaultFilter<ProjectResponse>('project_filter/', { group, filters }, { includeLang: false });
}

export async function fetchWasteGroups({ group, filters, lang }: FilterFetchParams) {
  return fetchDefaultFilter<WasteGroupResponse | unknown>('waste_group_filter/', { group, filters, lang });
}

const DEFAULT_FILTER_FETCHERS = {
  countries: fetchCountries,
  disposers: fetchDisposers,
  eurals: fetchEurals,
  locations: fetchLocations,
  materials: fetchMaterials,
  period: fetchPeriod,
  processes: fetchProcesses,
  processors: fetchProcessors,
  projects: fetchProjects,
  // wasteGroups: fetchWasteGroups,
} satisfies Partial<Record<keyof NewDefaultFilters, (params: FilterFetchParams) => Promise<unknown>>>;

type DefaultFilterFetchKey = keyof typeof DEFAULT_FILTER_FETCHERS;
type DefaultFilterFetcher = (typeof DEFAULT_FILTER_FETCHERS)[DefaultFilterFetchKey];

function createFilterRequestForKey(key: DefaultFilterFetchKey, filters: NewFilters): NewFilters {
  return {
    ...filters,
    [key]: [],
  };
}

/**
 * Fetches available options for every default filter from the current draft.
 * Each endpoint receives all draft filters except the option key it is loading.
 */
export async function fetchAllDefaultFilters(params: FilterFetchParams): Promise<NewDefaultFiltersData> {
  if (!isDateRange(params.filters?.period) || !params.group || !params.lang) return {};

  const entries = await Promise.all(
    (Object.entries(DEFAULT_FILTER_FETCHERS) as Array<[DefaultFilterFetchKey, DefaultFilterFetcher]>)?.map(
      async ([key, fetcher]) => {
        const value = await fetcher({
          ...params,
          filters: createFilterRequestForKey(key, params.filters),
        });

        return [key, value] as const;
      }
    )
  );

  return Object.fromEntries(entries) as NewDefaultFiltersData;
}
